import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { finalize, Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { NotificationHistoryService } from '../../../services/notifications/notification-history-service';
import { NotificationHistoryItem } from '../../../models/notification-history';

interface GroupedNotifications {
  label: string;
  items: NotificationHistoryItem[];
}

@Component({
  selector: 'app-notification-history',
  imports: [FormsModule, InputTextModule, SelectModule],
  templateUrl: './notification-history.html',
  styleUrl: './notification-history.scss',
})
export class NotificationHistory implements OnInit {
  private service = inject(NotificationHistoryService);

  categoryOptions = [
    { label: 'All categories', value: '' },
    { label: 'Promo',         value: 'PROMO' },
    { label: 'Alert',         value: 'ALERT' },
    { label: 'Reminder',      value: 'REMINDER' },
    { label: 'System',        value: 'SYSTEM' },
  ];
  selectedCategory = '';
  searchText = '';

  loading = signal(false);
  groups = signal<GroupedNotifications[]>([]);
  totalElements = 0;
  page = 0;
  hasNext = false;

  private search$ = new Subject<string>();

  ngOnInit() {
    this.search$.pipe(debounceTime(400), distinctUntilChanged()).subscribe(() => {
      this.page = 0;
      this.groups.set([]);
      this.loadHistory();
    });
  }

  init() {
    if (!this.groups().length) this.loadHistory();
  }

  refresh() {
    this.page = 0;
    this.groups.set([]);
    this.loadHistory();
  }

  onSearchChange(val: string) {
    this.searchText = val;
    this.search$.next(val);
  }

  onCategoryChange() {
    this.page = 0;
    this.groups.set([]);
    this.loadHistory();
  }

  loadHistory() {
    this.loading.set(true);
    this.service.getHistory({
      category: this.selectedCategory || undefined,
      search: this.searchText || undefined,
      page: this.page,
      size: 20,
    }).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: (res) => {
        const raw = res.data as NotificationHistoryItem[] | { content: NotificationHistoryItem[]; totalElements?: number; hasNext?: boolean };
        const items: NotificationHistoryItem[] = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.content) ? raw.content : [];
        this.totalElements = Array.isArray(raw) ? items.length : (raw?.totalElements ?? items.length);
        this.hasNext = Array.isArray(raw) ? false : (raw?.hasNext ?? false);
        this.groups.update(existing => this.mergeGroups(existing, items));
      },
    });
  }

  loadMore() {
    this.page++;
    this.loadHistory();
  }

categoryClass(cat: string): string {
    return 'cat-' + (cat ?? '').toLowerCase();
  }

  audienceLabel(type: string): string {
    const map: Record<string, string> = {
      'CUSTOMER_GROUP': 'Customer Group',
      'CUSTOMERS': 'Customers',
      'CUSTOMER_STATUS': 'By Status',
      'ALL_CUSTOMERS': 'All Customers',
    };
    return map[type] ?? type;
  }

  private mergeGroups(existing: GroupedNotifications[], items: NotificationHistoryItem[]): GroupedNotifications[] {
    const result = existing.map(g => ({ ...g, items: [...g.items] }));
    for (const item of items) {
      const label = this.dateGroupLabel(item.createdAt);
      const group = result.find(g => g.label === label);
      if (group) { group.items.push(item); }
      else { result.push({ label, items: [item] }); }
    }
    return result;
  }

  private dateGroupLabel(dateStr: string): string {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const d = new Date(dateStr);
    const itemDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (itemDay.getTime() === today.getTime()) return 'TODAY';
    if (itemDay.getTime() === yesterday.getTime()) return 'YESTERDAY';
    return 'EARLIER';
  }

  timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1)   return 'just now';
    if (mins < 60)  return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)   return `${hrs}h ago`;
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
}
